import { put, call, takeEvery } from "redux-saga/effects";
import { showErrorMsg, showSuccessMsg } from "../../utils/Helpers";
import {
  normalizeExportWorkbookBlob,
  parseContentDispositionFilename,
  readBlobErrorMessage,
  triggerBlobDownload,
} from "../../utils/reportExportDownload";

import * as apis from "../../network/apis/report";
import * as types from "./types";
import * as actions from "./actions";

function* getReportsListSaga({ payload }) {
  try {
    yield put(
      actions.getReportsResponse({
        list: [],
        meta: {},
      })
    );
    const response = yield call(apis.getReportList, payload);
    const rawList = response.data.data;
    yield put(
      actions.getReportsResponse({
        list: Array.isArray(rawList) ? rawList : [],
        meta: {
          currentPage: +response.headers["current-page"],
          count: +response.headers["total-count"],
          pages: +response.headers["total-pages"],
        },
      })
    );
    if (+response.headers["current-page"] > +response.headers["total-pages"]) {
      yield put(
        actions.getReportsRequest({
          ...payload,
          page: +response.headers["total-pages"],
        })
      );
    }
  } catch (error) {
    yield error.response && showErrorMsg(error.response?.data?.message);
  }
}

function* getReportDetailSaga({ payload }) {
  try {
    const response = yield call(apis.getReportDetail, payload);
    yield put(actions.getReportDetailResponse(response.data.data));
  } catch (error) {
    yield error.response && showErrorMsg(error.response?.data?.message);
  }
}

function* createReportSaga({ payload }) {
  const { id, data, onSuccess, onError } = payload || {};
  try {
    const response = yield call(apis.createReport, { id, data });
    yield put(actions.createReportResponse(response?.data?.data ?? null));
    const message = response?.data?.message;
    if (message) yield call(showSuccessMsg, message, true);
    if (typeof onSuccess === "function") onSuccess(response);
  } catch (error) {
    yield put(actions.createReportResponse(null));
    const message = error?.response?.data?.message;
    if (message) yield call(showErrorMsg, message);
    if (typeof onError === "function") onError(error);
  }
}

function* exportReportSheetListSaga({ payload }) {
  try {
    const response = yield call(apis.exportReportSheet, payload);
    const blob = response.data;

    // Axios blob responses can still be JSON error payloads with a 2xx/edge case.
    const contentType = String(response.headers?.["content-type"] || "");
    if (
      contentType.includes("application/json") ||
      (blob instanceof Blob && blob.type.includes("application/json"))
    ) {
      const text = yield call([blob, blob.text]);
      const parsed = JSON.parse(text);
      throw Object.assign(new Error(parsed?.message || "Export failed"), {
        response: { data: parsed },
      });
    }

    const rawBlob =
      blob instanceof Blob
        ? blob
        : new Blob([blob], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
    const fileBlob = yield call(normalizeExportWorkbookBlob, rawBlob);
    const filename =
      parseContentDispositionFilename(
        response.headers?.["content-disposition"],
      ) ||
      (payload?.id
        ? `logistics_issues_${payload.id}.xlsx`
        : "logistics_issues.xlsx");
    yield call(triggerBlobDownload, fileBlob, filename);
    yield call(showSuccessMsg, "File downloaded successfully!", true);
  } catch (error) {
    let message = error?.response?.data?.message || error?.message;
    if (!message || message === "Export failed") {
      const blobMessage = yield call(
        readBlobErrorMessage,
        error?.response?.data,
      );
      if (blobMessage) message = blobMessage;
    }
    if (message) yield call(showErrorMsg, message);
  }
}

function* updateReportSaga({ payload }) {
  const { event_id, id, data, onSuccess, onError } = payload || {};
  try {
    const response = yield call(apis.updateReport, { event_id, id, data });
    const next = response?.data?.data ?? null;
    yield put(actions.updateReportResponse(next));
    const message = response?.data?.message;
    if (message) yield call(showSuccessMsg, message, true);
    if (typeof onSuccess === "function") onSuccess(response);
  } catch (error) {
    yield put(actions.updateReportResponse(null));
    const message = error?.response?.data?.message;
    if (message) yield call(showErrorMsg, message);
    if (typeof onError === "function") onError(error);
  }
}

function* getReportCommentsSaga({ payload }) {
  const { id, params } = payload || {};
  try {
    const response = yield call(apis.getReportComments, { id, params });
    yield put(
      actions.getReportCommentsResponse(
        Array.isArray(response?.data?.data) ? response.data.data : [],
      ),
    );
  } catch (error) {
    yield put(actions.getReportCommentsResponse([]));
    const message = error?.response?.data?.message;
    if (message) yield call(showErrorMsg, message);
  }
}

function* createReportCommentSaga({ payload }) {
  const { id, data, onSuccess, onError } = payload || {};
  try {
    const response = yield call(apis.createReportComment, { id, data });
    yield put(
      actions.createReportCommentResponse(response?.data?.data ?? null),
    );
    const message = response?.data?.message;
    if (message) yield call(showSuccessMsg, message, true);
    if (typeof onSuccess === "function") onSuccess(response);
  } catch (error) {
    yield put(actions.createReportCommentResponse(null));
    const message = error?.response?.data?.message;
    if (message) yield call(showErrorMsg, message);
    if (typeof onError === "function") onError(error);
  }
}

export default function* ReportSagas() {
  yield takeEvery(types.GET_REPORTS_REQUEST, getReportsListSaga);
  yield takeEvery(types.GET_REPORT_DETAIL_REQUEST, getReportDetailSaga);
  yield takeEvery(types.CREATE_REPORT_REQUEST, createReportSaga);
  yield takeEvery(types.EXPORT_REPORT_SHEET_REQUEST, exportReportSheetListSaga);
  yield takeEvery(types.UPDATE_REPORT_REQUEST, updateReportSaga);
  yield takeEvery(types.GET_REPORT_COMMENTS_REQUEST, getReportCommentsSaga);
  yield takeEvery(types.CREATE_REPORT_COMMENT_REQUEST, createReportCommentSaga);
}
